import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { CbmImpl } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { getReservationsByTask, isEmailTask, sendTrackingEvent } from "~/modules/actions/ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";




export enum ParticipantLevel {
    To = "to",
    CC = "cc"
}



export interface AddEmailParticipantOptions {
    name: string;
}





















export class AddEmailParticipant implements Action<Promise<void>> {
    #taskSid: string;

    #email: string;

    #level: ParticipantLevel;

    #options?: AddEmailParticipantOptions;

    constructor(taskSid: string, email: string, level: ParticipantLevel, options?: AddEmailParticipantOptions) {
        this.#taskSid = taskSid;
        this.#email = email;
        this.#level = level;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const reservation = getReservationsByTask(worker, this.#taskSid)[0];

        if (!reservation) {
            const errorMsg = `AddEmailParticipant: Reservation for task ${this.#taskSid} not found`;

            sendTrackingEvent(EVENTS.AddEmailParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantLevel: this.#level
            });

            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const task = reservation.task;

        if (!isEmailTask(task)) {
            const errorMsg = `AddEmailParticipant: Task ${this.#taskSid} is not an email task`;
            sendTrackingEvent(EVENTS.AddEmailParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantLevel: this.#level
            });

            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            const participant = await cbm.addEmailParticipant(task, {
                address: this.#email,
                level: this.#level,
                toName: this.#options?.name || ""
            });
            sendTrackingEvent(EVENTS.AddEmailParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                participantLevel: this.#level,
                participantSid: participant.participantSid
            });
            return Promise.resolve();
        } catch (error) {
            const errorMsg = `AddEmailParticipant: Failed to add participant to task ${this.#taskSid}`;
            sendTrackingEvent(EVENTS.AddEmailParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantLevel: this.#level
            });
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }
}
